import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    Search, Plus, Archive, RotateCcw, Pencil, Mail, Phone, User, ArrowLeft,
    AlertTriangle, ArrowUpDown, ArrowDown, ArrowUp, RefreshCw, Zap, Inbox,
} from "lucide-react";

import InstallerShell from "@/components/installer/InstallerShell";
import { PageContainer, PageHeader } from "@/components/layout/PageLayout";
import { EngineerForm } from "@/components/installer/EngineerForm";
import { EngineerAvatar } from "@/components/installer/EngineerAvatar";
import { api, formatApiError } from "@/lib/api";
import { effectiveResourceType, resourceSearchBag } from "@/lib/resourceType";
import { buildTaskDrillUrl } from "@/lib/navLinks";

/**
 * Engineer Management (Settings → Engineers).
 *
 * The single source of truth for every engineer picker across SolarFlow.
 * Extended (Phase 2) with real-time workload metrics — Open / Overdue /
 * Critical / Due Today / Visits Today / Visits This Week — sourced from
 * /api/engineers/workload-summary. Every count drills into the existing
 * Tasks or Calendar module (never a duplicate list).
 */

const SORT_KEYS = [
    { key: "displayName",         label: "Name",           numeric: false },
    { key: "openTaskCount",       label: "Open",           numeric: true  },
    { key: "overdueTaskCount",    label: "Overdue",        numeric: true  },
    { key: "criticalTaskCount",   label: "Critical",       numeric: true  },
    { key: "dueTodayTaskCount",   label: "Due Today",      numeric: true  },
    { key: "visitsTodayCount",    label: "Visits Today",   numeric: true  },
    { key: "visitsThisWeekCount", label: "Visits Wk",      numeric: true  },
];

const WORKLOAD_FILTERS = [
    { id: "has_open",     label: "Has open",     match: (r) => r.openTaskCount > 0 },
    { id: "has_overdue",  label: "Has overdue",  match: (r) => r.overdueTaskCount > 0 },
    { id: "has_critical", label: "Has critical", match: (r) => r.criticalTaskCount > 0 },
    { id: "no_open",      label: "No open",      match: (r) => r.openTaskCount === 0 },
    { id: "visits_today", label: "Visits today", match: (r) => r.visitsTodayCount > 0 },
];

export default function EngineerAdmin({ shell = "installer", eyebrow, backFallback, canManage: canManageProp, variant = "full", title, hideHeader = false, mode = "full" } = {}) {
    const navigate = useNavigate();
    // FIELD-001C · URL-persisted Resource Management filters. Only the
    // Operations surface uses URL sync — the legacy /settings/engineers
    // route keeps its transient state.
    const [searchParams, setSearchParams] = useSearchParams();
    const withShell = shell === "installer";
    const inOperations = shell === "operations";
    // FIELD-001C1B · Terminology gate. In Operations surfaces every
    // umbrella "Engineer" label reads as "Resource". The installer
    // shell (legacy `/settings/engineers`) keeps the classic wording.
    const RES = inOperations ? "resource" : "engineer";
    const RESES = inOperations ? "resources" : "engineers";
    const ResSingular = inOperations ? "Resource" : "Engineer";
    const AddLabel = inOperations ? "Add resource" : "Add engineer";
    // MVP-ENG-001 · Permission-aware controls. When rendered inside the
    // canonical Operations Engineers workspace the parent decides
    // whether restricted create / edit / archive / configuration
    // actions are exposed via `canManage`. Installer-shell keeps the
    // legacy behaviour of "installer role implies manage" — that's
    // enforced by the backend and the `/settings/engineers` route
    // guard.
    const canManage = canManageProp !== undefined ? !!canManageProp : true;
    // MVP-ENG-001A · Variant switches which tabbed surface renders:
    //   * full — legacy behaviour (roster + workload columns);
    //   * roster — identity / contact / role / actions only;
    //   * workload — identity + workload metrics (no Contact column);
    //   * administration — full table + prominent archived toggle
    //                       + create/edit/archive controls.
    const showContact = variant === "full" || variant === "roster" || variant === "administration";
    const showWorkloadCols = variant === "full" || variant === "workload" || variant === "administration";
    const showUnassignedStrip = variant === "full" || variant === "workload";
    const showWorkloadFilters = variant === "full" || variant === "workload" || variant === "administration";
    const [engineers, setEngineers] = useState([]);
    const [workload, setWorkload] = useState({ engineers: [], unassigned: {}, week_start: null });
    const [palette, setPalette] = useState([]);
    const [showArchived, setShowArchived] = useState(() =>
        inOperations && searchParams.get("archived") === "1",
    );
    const [query, setQuery] = useState(() =>
        inOperations ? (searchParams.get("q") || "") : "",
    );
    const [workloadFilter, setWorkloadFilter] = useState(() => {
        if (!inOperations) return null;
        const w = searchParams.get("workload");
        return w || null;
    });
    const [sort, setSort] = useState(() => {
        if (!inOperations) return { key: "displayName", dir: "asc" };
        const k = searchParams.get("sort") || "displayName";
        const d = searchParams.get("dir") === "desc" ? "desc" : "asc";
        return { key: k, dir: d };
    });
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [editing, setEditing] = useState(null);
    const [busyId, setBusyId] = useState(null);
    // FIELD-001C1B · Resource Management "View resource" summary modal.
    const [viewing, setViewing] = useState(null);

    const [loadError, setLoadError] = useState(null);
    const refresh = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        setRefreshing(true);
        try {
            const [engs, wl] = await Promise.all([
                api.listEngineers({ includeArchived: true }),
                api.engineerWorkloadSummary(),
            ]);
            setEngineers(engs);
            setWorkload(wl);
            setLoadError(null);
        } catch (e) {
            // FIELD-001C UAT · Distinguish auth failure from an empty
            // roster. The axios interceptor handles token refresh /
            // logout for a 401 automatically; here we just surface a
            // retryable error banner instead of a misleading
            // "0 active / No resources" empty state.
            const status = e?.response?.status;
            setLoadError({
                status,
                kind: status === 401 || status === 403 ? "auth" : "network",
                message: formatApiError(e.response?.data?.detail)
                       || e.message
                       || "Failed to load resources",
            });
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to load engineers");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        refresh();
        api.engineerPalette().then(setPalette).catch(() => setPalette([]));
    }, [refresh]);

    // FIELD-001C · Round-trip Resource Management filters through the
    // URL. Debounced write (`replace: true`) so keystrokes don't
    // pollute history but a direct link / refresh / Back-Forward
    // still lands on the same visible state. `highlight` and other
    // unrelated params are preserved verbatim.
    useEffect(() => {
        if (!inOperations) return;
        const timer = setTimeout(() => {
            const next = new URLSearchParams(searchParams);
            const set = (k, v) => { if (v) next.set(k, v); else next.delete(k); };
            set("q", query.trim() || null);
            set("archived", showArchived ? "1" : null);
            set("workload", workloadFilter || null);
            set("sort", sort.key !== "displayName" ? sort.key : null);
            set("dir", sort.dir === "desc" ? "desc" : null);
            const current = searchParams.toString();
            const proposed = next.toString();
            if (current !== proposed) {
                setSearchParams(next, { replace: true });
            }
        }, 200);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inOperations, query, showArchived, workloadFilter, sort.key, sort.dir]);

    // Merge engineer roster with workload row (roster wins for identity fields;
    // workload wins for counts). This keeps the page functional even if the
    // workload endpoint is momentarily out-of-sync.
    const rows = useMemo(() => {
        const wlById = Object.fromEntries((workload.engineers || []).map((r) => [r.id, r]));
        return engineers.map((e) => ({
            ...e,
            openTaskCount:        wlById[e.id]?.openTaskCount ?? 0,
            overdueTaskCount:     wlById[e.id]?.overdueTaskCount ?? 0,
            criticalTaskCount:    wlById[e.id]?.criticalTaskCount ?? 0,
            dueTodayTaskCount:    wlById[e.id]?.dueTodayTaskCount ?? 0,
            completedTodayTaskCount: wlById[e.id]?.completedTodayTaskCount ?? 0,
            visitsTodayCount:     wlById[e.id]?.visitsTodayCount ?? 0,
            visitsThisWeekCount:  wlById[e.id]?.visitsThisWeekCount ?? 0,
            reassignmentRequired: wlById[e.id]?.reassignmentRequired ?? 0,
        }));
    }, [engineers, workload]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        const filterDef = WORKLOAD_FILTERS.find((f) => f.id === workloadFilter);
        return rows.filter((e) => {
            if (!showArchived && !e.active) return false;
            if (showArchived && e.active) return false;
            if (q) {
                // FIELD-001C · Search matches the SAME effective-type the
                // row displays, plus name / organisation / email / mobile /
                // legacy role. Prevents the "General shows on row but is
                // excluded by search" divergence.
                if (!resourceSearchBag(e).includes(q)
                    && ![e.firstName, e.lastName, e.employeeNumber]
                        .map((v) => (v || "").toString().toLowerCase())
                        .join(" ").includes(q)) {
                    return false;
                }
            }
            if (filterDef && !filterDef.match(e)) return false;
            return true;
        });
    }, [rows, query, showArchived, workloadFilter]);

    const sorted = useMemo(() => {
        const meta = SORT_KEYS.find((k) => k.key === sort.key) || SORT_KEYS[0];
        const factor = sort.dir === "desc" ? -1 : 1;
        const list = [...filtered];
        list.sort((a, b) => {
            const av = a[sort.key];
            const bv = b[sort.key];
            if (meta.numeric) {
                return ((av || 0) - (bv || 0)) * factor;
            }
            return (av || "").toString().localeCompare((bv || "").toString()) * factor;
        });
        return list;
    }, [filtered, sort]);

    const activeCount = engineers.filter((e) => e.active).length;
    const archivedCount = engineers.length - activeCount;
    const reassignmentTotal = engineers
        .filter((e) => !e.active)
        .reduce((sum, e) => sum + (rows.find((r) => r.id === e.id)?.reassignmentRequired || 0), 0);

    const doArchive = async (eng) => {
        setBusyId(eng.id);
        try {
            const updated = await api.archiveEngineer(eng.id);
            setEngineers((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
            toast.success(`${updated.displayName} archived`);
            refresh({ silent: true });
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Archive failed");
        } finally { setBusyId(null); }
    };
    const doReactivate = async (eng) => {
        setBusyId(eng.id);
        try {
            const updated = await api.reactivateEngineer(eng.id);
            setEngineers((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
            toast.success(`${updated.displayName} reactivated`);
            refresh({ silent: true });
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Reactivate failed");
        } finally { setBusyId(null); }
    };

    // FIELD-001C · Drill-down helpers. Delegates to the shared
    // `buildTaskDrillUrl` builder in `@/lib/navLinks` so the
    // scope/priority defaulting rules are unit-tested in one place
    // and can't drift between the Workload counters, the
    // Unassigned strip and the Resource Summary modal.
    const drillTasks = ({ engineer_id, ...extras } = {}) => {
        navigate(buildTaskDrillUrl(engineer_id, extras, { inOperations }));
    };
    const drillCalendar = (params) => {
        const qs = new URLSearchParams(params).toString();
        navigate(`${inOperations ? "/operations/calendar" : "/installer/calendar"}?${qs}`);
    };
    const openEngineerTasks = (eng, extra = {}) =>
        drillTasks({ engineer_id: eng.id, ...extra });

    // Sort handler
    const clickSort = (key) => {
        setSort((s) => s.key === key
            ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
            : { key, dir: SORT_KEYS.find((k) => k.key === key)?.numeric ? "desc" : "asc" });
    };

    const resetFilters = () => {
        setQuery("");
        setWorkloadFilter(null);
    };

    return (
        <ShellWrapper withShell={withShell}>
            <PageContainer width="medium" testid="engineer-admin-page">
                {!hideHeader && (
                <PageHeader
                    eyebrow={eyebrow || (inOperations ? "Operations" : "Settings")}
                    title={title || (inOperations ? "Resource Management" : "Engineers")}
                    subtitle={`${activeCount} active · ${archivedCount} archived. Only active ${RESES} appear in ${inOperations ? "resource and appointment" : "appointment"} pickers.`}
                    backFallback={backFallback || (inOperations ? "/operations" : "/installer")}
                    testid="engineers-header"
                    rightSlot={(
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => refresh()}
                                data-testid="engineers-refresh"
                                aria-label="Refresh workload"
                                className="inline-flex items-center justify-center h-11 w-11 rounded-md border border-[#E5E7EB] bg-white text-[#4B5565] hover:bg-[#F5F5F7] disabled:opacity-60"
                                disabled={refreshing}
                                title="Refresh workload"
                            >
                                <RefreshCw size={16} strokeWidth={1.75} className={refreshing ? "animate-spin" : ""} />
                            </button>
                            {canManage && (
                                <button
                                    data-testid="engineers-add"
                                    onClick={() => setEditing("new")}
                                    className="pe-btn-primary h-11"
                                >
                                    <Plus size={14} strokeWidth={2} /> {AddLabel}
                                </button>
                            )}
                        </div>
                    )}
                />
                )}
                {hideHeader && canManage && (
                    <div className="mb-4 flex justify-end" data-testid="engineers-header-hidden-actions">
                        <button
                            onClick={() => refresh()}
                            data-testid="engineers-refresh"
                            aria-label="Refresh workload"
                            className="inline-flex items-center justify-center h-11 w-11 rounded-md border border-[#E5E7EB] bg-white text-[#4B5565] hover:bg-[#F5F5F7] disabled:opacity-60 mr-2"
                            disabled={refreshing}
                            title="Refresh workload"
                        >
                            <RefreshCw size={16} strokeWidth={1.75} className={refreshing ? "animate-spin" : ""} />
                        </button>
                        <button
                            data-testid="engineers-add"
                            onClick={() => setEditing("new")}
                            className="pe-btn-primary h-11"
                        >
                            <Plus size={14} strokeWidth={2} /> {AddLabel}
                        </button>
                    </div>
                )}

                {/* Unassigned Work strip — only shown on workload-focused variants */}
                {showUnassignedStrip && (
                    <UnassignedStrip
                        unassigned={workload.unassigned}
                        onTasks={() => drillTasks({ engineer_id: "__unassigned__" })}
                        onOverdue={() => drillTasks({ engineer_id: "__unassigned__", scope: "overdue" })}
                        onCalendar={() => drillCalendar({ engineer_filter: "__unassigned__", focus: "week" })}
                    />
                )}                {/* Search + tabs */}
                <div className="mt-5 flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-[220px]">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5565]" />
                        <input
                            data-testid="engineers-search"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={inOperations
                                ? "Search by name, organisation or resource type…"
                                : "Search by name, email, mobile or role…"}
                            className="w-full h-11 pl-10 pr-3 rounded-md border border-[#E5E7EB] bg-white text-base outline-none focus:border-[#3E9E2E]"
                        />
                    </div>
                    <div className="flex bg-white border border-[#E5E7EB] rounded-md p-1 shrink-0" role="tablist" data-testid="engineers-filter">
                        <FilterChip label={`Active (${activeCount})`} active={!showArchived} onClick={() => setShowArchived(false)} testid="filter-active" />
                        <FilterChip label={`Archived (${archivedCount})`} active={showArchived} onClick={() => setShowArchived(true)} testid="filter-archived" />
                    </div>
                </div>

                {/* Workload filter chips — only on workload / admin variants */}
                {showWorkloadFilters && (
                <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="workload-filters">
                    <span className="text-xs uppercase tracking-[0.12em] text-[#4B5565] mr-1">Workload</span>
                    {WORKLOAD_FILTERS.map((f) => (
                        <FilterPill
                            key={f.id}
                            label={f.label}
                            active={workloadFilter === f.id}
                            onClick={() => setWorkloadFilter((prev) => prev === f.id ? null : f.id)}
                            testid={`workload-filter-${f.id}`}
                        />
                    ))}
                    {(workloadFilter || query) && (
                        <button
                            onClick={resetFilters}
                            data-testid="workload-filter-reset"
                            className="text-xs text-[#4B5565] underline underline-offset-2 hover:text-[#131B2F]"
                        >
                            Reset
                        </button>
                    )}
                </div>
                )}

                {showArchived && reassignmentTotal > 0 && (
                    <div
                        data-testid="reassignment-banner"
                        className="mt-4 rounded-lg border border-[#F59E0B]/35 bg-[#FEF3C7]/60 px-4 py-3 flex items-center gap-3 text-sm text-[#7C2D12]"
                    >
                        <AlertTriangle size={16} strokeWidth={1.75} />
                        <span>
                            <strong>{reassignmentTotal}</strong> open task{reassignmentTotal === 1 ? "" : "s"} across archived engineers still require reassignment.
                        </span>
                    </div>
                )}

                {/* Table */}
                <div className="mt-4 bg-white rounded-[20px] border border-[#E5E7EB] overflow-x-auto" data-testid="engineers-table">
                    {loading ? (
                        <div className="p-10 text-center text-sm text-[#4B5565]" data-testid="engineers-loading">Loading…</div>
                    ) : loadError ? (
                        // FIELD-001C UAT · Auth / network failure must NOT
                        // look like an empty roster. The axios interceptor
                        // handles token refresh + logout for 401s; here we
                        // surface a retryable banner so the operator can
                        // distinguish "no resources" from "load failed".
                        <div className="p-10 text-center" data-testid="engineers-load-error">
                            <User size={32} strokeWidth={1.5} className="mx-auto text-[#DA3535]" />
                            <div className="mt-2 text-base text-[#131B2F] font-medium">
                                {loadError.kind === "auth" ? "Sign-in required to view resources" : "Couldn’t load resources"}
                            </div>
                            <div className="text-sm text-[#4B5565] mt-1" data-testid="engineers-load-error-message">
                                {loadError.kind === "auth"
                                    ? "Your session may have expired. Please refresh or sign in again."
                                    : loadError.message}
                            </div>
                            <button type="button" onClick={() => refresh()}
                                    data-testid="engineers-load-error-retry"
                                    className="mt-3 pe-btn-outline h-9">
                                Retry
                            </button>
                        </div>
                    ) : sorted.length === 0 ? (
                        <div className="p-10 text-center" data-testid="engineers-empty">
                            <User size={32} strokeWidth={1.5} className="mx-auto text-[#D1D5DB]" />
                            <div className="mt-2 text-base text-[#131B2F] font-medium">No {RESES} to show</div>
                            <div className="text-sm text-[#4B5565] mt-1">
                                {query || workloadFilter
                                    ? "Try clearing filters."
                                    : showArchived ? `No archived ${RESES}.` : `Add your first ${RES} to get started.`}
                            </div>
                        </div>
                    ) : (
                        <table className="w-full text-sm min-w-[880px]" data-testid={`engineers-table-variant-${variant}`}>
                            <thead className="bg-[#F5F5F7] text-[#4B5565]">
                                <tr>
                                    <SortableTh sort={sort} onClick={clickSort} sortKey="displayName" testid="th-name">{ResSingular}</SortableTh>
                                    {showContact && <Th className="hidden lg:table-cell">Contact</Th>}
                                    <Th className="hidden md:table-cell">{inOperations ? "Resource type" : "Role"}</Th>
                                    {showWorkloadCols && <SortableTh sort={sort} onClick={clickSort} sortKey="openTaskCount" testid="th-open" numeric className="hidden md:table-cell">Open</SortableTh>}
                                    {showWorkloadCols && <SortableTh sort={sort} onClick={clickSort} sortKey="overdueTaskCount" testid="th-overdue" numeric className="hidden md:table-cell">Overdue</SortableTh>}
                                    {showWorkloadCols && <SortableTh sort={sort} onClick={clickSort} sortKey="criticalTaskCount" testid="th-critical" numeric className="hidden lg:table-cell">Critical</SortableTh>}
                                    {showWorkloadCols && <SortableTh sort={sort} onClick={clickSort} sortKey="dueTodayTaskCount" testid="th-due-today" numeric className="hidden xl:table-cell" title="Due today">Due</SortableTh>}
                                    {showWorkloadCols && <SortableTh sort={sort} onClick={clickSort} sortKey="visitsTodayCount" testid="th-visits-today" numeric className="hidden lg:table-cell" title="Visits today">Today</SortableTh>}
                                    {showWorkloadCols && <SortableTh sort={sort} onClick={clickSort} sortKey="visitsThisWeekCount" testid="th-visits-week" numeric className="hidden xl:table-cell" title="Visits this week">Wk</SortableTh>}
                                    <Th className="text-right sticky right-0 z-10 bg-[#F5F5F7] shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.08)]">Actions</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {sorted.map((eng) => (
                                    <EngineerRow
                                        key={eng.id}
                                        eng={eng}
                                        busy={busyId === eng.id}
                                        canManage={canManage}
                                        showContact={showContact}
                                        showWorkloadCols={showWorkloadCols}
                                        inOperations={inOperations}
                                        onView={() => setViewing(eng)}
                                        onEdit={() => setEditing(eng)}
                                        onArchive={() => doArchive(eng)}
                                        onReactivate={() => doReactivate(eng)}
                                        drillTasks={openEngineerTasks}
                                        drillCalendar={drillCalendar}
                                    />
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </PageContainer>

            <EngineerForm
                open={!!editing && canManage}
                engineer={editing === "new" ? null : editing}
                palette={palette}
                mode={mode}
                onClose={() => setEditing(null)}
                onSaved={(saved) => {
                    setEngineers((prev) => {
                        const idx = prev.findIndex((x) => x.id === saved.id);
                        return idx >= 0 ? prev.map((x) => (x.id === saved.id ? saved : x)) : [...prev, saved];
                    });
                    setEditing(null);
                    refresh({ silent: true });
                }}
                workloadRow={
                    editing && editing !== "new"
                        ? rows.find((r) => r.id === editing.id)
                        : null
                }
                onDrillTasks={(row, extra) => { setEditing(null); openEngineerTasks(row, extra); }}
                onDrillCalendar={(row, extra) => { setEditing(null); drillCalendar({ engineer_filter: row.id, ...extra }); }}
            />

            {/* FIELD-001C1B · Read-only "View resource" summary modal. */}
            <ResourceSummaryModal
                eng={viewing}
                onClose={() => setViewing(null)}
                onDrillTasks={(extra) => { const r = viewing; setViewing(null); openEngineerTasks(r, extra); }}
                onDrillCalendar={(extra) => { const r = viewing; setViewing(null); drillCalendar({ engineer_filter: r.id, ...extra }); }}
            />
        </ShellWrapper>
    );
}

// MVP-ENG-001 · Optional shell wrapper — installer route retains the
// legacy `<InstallerShell>` wrapping; Operations route delegates
// wrapping to `<OperationsShell>` in the parent page.
function ShellWrapper({ withShell, children }) {
    if (!withShell) return <>{children}</>;
    return <InstallerShell>{children}</InstallerShell>;
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function EngineerRow({ eng, busy, canManage = true, showContact = true, showWorkloadCols = true, inOperations = false, onView, onEdit, onArchive, onReactivate, drillTasks, drillCalendar }) {
    const archived = !eng.active;
    // FIELD-001C · Shared effective-type resolver — display + search
    // stay in lockstep across every Resource Management surface.
    const resourceLabel = inOperations
        ? effectiveResourceType(eng)
        : eng.role;
    return (
        <tr data-testid={`eng-row-${eng.id}`} className={`group border-t border-[#E5E7EB] bg-white hover:bg-[#F9FAFB] ${archived ? "opacity-90" : ""}`}>
            <td className="p-3">
                <div className="flex items-center gap-3">
                    <EngineerAvatar eng={eng} size={36} />
                    <div className="min-w-0 max-w-[220px]">
                        <div className="flex items-center gap-2">
                            <div className="text-base text-[#131B2F] font-medium truncate" data-testid={`eng-name-${eng.id}`}>{eng.displayName}</div>
                            {eng.email && (
                                <div className="text-xs text-[#4B5565] truncate" data-testid={`eng-email-${eng.id}`}>
                                    {eng.email}
                                </div>
                            )}
                            {eng.linkedUserId && (
                                <div className="mt-0.5" data-testid={`eng-linked-badge-${eng.id}`}>
                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#EEF2FF] text-[#1E3A8A]">
                                        Linked login
                                    </span>
                                </div>
                            )}
                            {archived && (
                                <span className="text-[10px] uppercase tracking-[0.12em] text-[#7C2D12] bg-[#FEF3C7] px-1.5 py-0.5 rounded" data-testid={`eng-archived-tag-${eng.id}`}>
                                    Archived
                                </span>
                            )}
                        </div>
                        <div className="text-xs text-[#4B5565] md:hidden truncate">{resourceLabel}</div>

                        {/* Mobile / iPad Split View compact workload */}
                        <div className="mt-1 md:hidden text-xs text-[#4B5565]" data-testid={`eng-compact-${eng.id}`}>
                            <button
                                onClick={() => drillTasks(eng)}
                                className="hover:underline"
                                aria-label={`View ${eng.openTaskCount} open tasks assigned to ${eng.displayName}`}
                            >
                                {eng.openTaskCount} open
                            </button>
                            {eng.overdueTaskCount > 0 && (
                                <>
                                    <span> · </span>
                                    <button
                                        onClick={() => drillTasks(eng, { scope: "overdue" })}
                                        className="text-[#B45309] hover:underline"
                                        aria-label={`View ${eng.overdueTaskCount} overdue tasks assigned to ${eng.displayName}`}
                                    >
                                        {eng.overdueTaskCount} overdue
                                    </button>
                                </>
                            )}
                            {eng.criticalTaskCount > 0 && (
                                <>
                                    <span> · </span>
                                    <button
                                        onClick={() => drillTasks(eng, { priority: "Critical" })}
                                        className="text-[#991B1B] hover:underline"
                                        aria-label={`View ${eng.criticalTaskCount} critical tasks assigned to ${eng.displayName}`}
                                    >
                                        {eng.criticalTaskCount} critical
                                    </button>
                                </>
                            )}
                        </div>

                        {archived && eng.reassignmentRequired > 0 && (
                            <div
                                className="mt-1 inline-flex items-center gap-1 text-xs text-[#7C2D12] bg-[#FEF3C7] px-1.5 py-0.5 rounded"
                                data-testid={`eng-reassignment-${eng.id}`}
                            >
                                <AlertTriangle size={12} strokeWidth={1.75} />
                                {eng.reassignmentRequired} open task{eng.reassignmentRequired === 1 ? "" : "s"} require reassignment
                            </div>
                        )}
                    </div>
                </div>
            </td>

            {showContact && (
            <td className="p-3 hidden lg:table-cell text-[#4B5565] max-w-[180px]">
                <div className="flex flex-col text-xs">
                    <span className="inline-flex items-center gap-1.5 truncate"><Mail size={12} strokeWidth={1.75} /> {eng.email}</span>
                    {eng.mobileNumber && (
                        <span className="inline-flex items-center gap-1.5 truncate mt-0.5"><Phone size={12} strokeWidth={1.75} /> {eng.mobileNumber}</span>
                    )}
                </div>
            </td>
            )}

            <td className="p-3 hidden md:table-cell text-[#131B2F]">{resourceLabel}</td>

            {showWorkloadCols && (<>
            <MetricCell
                value={eng.openTaskCount}
                onClick={() => drillTasks(eng)}
                testid={`eng-metric-open-${eng.id}`}
                srLabel={`View ${eng.openTaskCount} open tasks assigned to ${eng.displayName}`}
            />
            <MetricCell
                value={eng.overdueTaskCount}
                tone={eng.overdueTaskCount > 0 ? "warning" : "neutral"}
                onClick={() => drillTasks(eng, { scope: "overdue" })}
                testid={`eng-metric-overdue-${eng.id}`}
                srLabel={`View ${eng.overdueTaskCount} overdue tasks assigned to ${eng.displayName}`}
            />
            <MetricCell
                value={eng.criticalTaskCount}
                tone={eng.criticalTaskCount > 0 ? "critical" : "neutral"}
                onClick={() => drillTasks(eng, { priority: "Critical" })}
                testid={`eng-metric-critical-${eng.id}`}
                className="hidden lg:table-cell"
                srLabel={`View ${eng.criticalTaskCount} critical tasks assigned to ${eng.displayName}`}
            />
            <MetricCell
                value={eng.dueTodayTaskCount}
                onClick={() => drillTasks(eng, { scope: "due_today" })}
                testid={`eng-metric-due-today-${eng.id}`}
                className="hidden xl:table-cell"
                srLabel={`View ${eng.dueTodayTaskCount} tasks due today assigned to ${eng.displayName}`}
            />
            <MetricCell
                value={eng.visitsTodayCount}
                onClick={() => drillCalendar({ engineer_filter: eng.id, focus: "today" })}
                testid={`eng-metric-visits-today-${eng.id}`}
                className="hidden lg:table-cell"
                srLabel={`View ${eng.visitsTodayCount} visits scheduled today for ${eng.displayName}`}
            />
            <MetricCell
                value={eng.visitsThisWeekCount}
                onClick={() => drillCalendar({ engineer_filter: eng.id, focus: "week" })}
                testid={`eng-metric-visits-week-${eng.id}`}
                className="hidden xl:table-cell"
                srLabel={`View ${eng.visitsThisWeekCount} visits scheduled this week for ${eng.displayName}`}
            />
            </>)}

            <td className="p-3 text-right sticky right-0 z-10 bg-white group-hover:bg-[#F9FAFB] shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.08)]">
                <div className="inline-flex gap-1.5">
                    {canManage ? (
                        <>
                            <RowBtn label="Edit" icon={Pencil} onClick={onEdit} testid={`eng-edit-${eng.id}`} />
                            {eng.active ? (
                                <RowBtn label="Archive" icon={Archive} onClick={onArchive} disabled={busy} tone="amber" testid={`eng-archive-${eng.id}`} />
                            ) : (
                                <RowBtn label="Reactivate" icon={RotateCcw} onClick={onReactivate} disabled={busy} tone="green" testid={`eng-reactivate-${eng.id}`} />
                            )}
                        </>
                    ) : inOperations ? (
                        // FIELD-001C1B · Resource Management row action —
                        // non-administrative "View resource" summary.
                        <RowBtn label="View" icon={User} onClick={onView}
                                testid={`eng-view-${eng.id}`} />
                    ) : (
                        <span
                            className="text-[11px] text-[#4B5565]"
                            data-testid={`eng-readonly-${eng.id}`}
                        >
                            View only
                        </span>
                    )}
                </div>
            </td>
        </tr>
    );
}

function MetricCell({ value, onClick, tone = "neutral", testid, className = "", srLabel }) {
    const toneCls = {
        neutral:  "text-[#131B2F]",
        warning:  "text-[#B45309] bg-[#F59E0B]/10",
        critical: "text-[#991B1B] bg-[#FEE2E2]",
    }[tone];
    const showBadge = tone !== "neutral" && value > 0;
    return (
        <td className={`p-3 text-center ${className}`}>
            <button
                onClick={onClick}
                data-testid={testid}
                aria-label={srLabel}
                className={`inline-flex items-center justify-center min-w-[2.5rem] h-8 px-2 rounded-md text-sm font-medium transition-colors hover:bg-[#F5F5F7] ${showBadge ? toneCls : "text-[#131B2F]"} ${value === 0 ? "text-[#9CA3AF]" : ""}`}
            >
                {value}
            </button>
        </td>
    );
}

function UnassignedStrip({ unassigned, onTasks, onOverdue, onCalendar }) {
    const u = unassigned || {};
    const open = u.openTaskCount || 0;
    const overdue = u.overdueTaskCount || 0;
    const visits = u.visitsThisWeekCount || 0;
    return (
        <div
            className="mt-5 rounded-xl border border-[#E5E7EB] bg-white p-4 flex flex-wrap items-center gap-4"
            data-testid="unassigned-strip"
        >
            <div className="flex items-center gap-2 text-sm text-[#4B5565]">
                <Inbox size={16} strokeWidth={1.75} />
                <span className="uppercase tracking-[0.12em] text-[11px]">Unassigned work</span>
            </div>
            <UnassignedStat label="Open tasks" value={open} onClick={onTasks}   testid="unassigned-open" />
            <UnassignedStat label="Overdue"    value={overdue} onClick={onOverdue} tone="warning" testid="unassigned-overdue" />
            <UnassignedStat label="Visits this week" value={visits} onClick={onCalendar} testid="unassigned-visits" />
        </div>
    );
}

function UnassignedStat({ label, value, onClick, tone = "neutral", testid }) {
    const toneCls = {
        neutral:  "text-[#131B2F]",
        warning:  value > 0 ? "text-[#B45309]" : "text-[#131B2F]",
    }[tone];
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            aria-label={`View ${value} unassigned ${label.toLowerCase()}`}
            className={`inline-flex items-baseline gap-1.5 hover:underline`}
        >
            <span className={`text-lg font-semibold ${toneCls}`}>{value}</span>
            <span className="text-xs text-[#4B5565]">{label}</span>
        </button>
    );
}

function SortableTh({ children, sortKey, sort, onClick, numeric, testid, className = "", title }) {
    const active = sort.key === sortKey;
    const dir = active ? sort.dir : null;
    const Icon = !active ? ArrowUpDown : dir === "asc" ? ArrowUp : ArrowDown;
    return (
        <th className={`p-3 text-[11px] font-medium tracking-[0.08em] uppercase ${numeric ? "text-center" : "text-left"} ${className}`} title={title}>
            <button
                onClick={() => onClick(sortKey)}
                data-testid={testid}
                aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
                aria-label={title || (typeof children === "string" ? children : undefined)}
                className={`inline-flex items-center gap-1 hover:text-[#131B2F] transition-colors ${active ? "text-[#131B2F]" : ""}`}
            >
                {children}
                <Icon size={12} strokeWidth={2} className={active ? "opacity-100" : "opacity-40"} />
            </button>
        </th>
    );
}

function Th({ children, className = "" }) {
    return <th className={`p-3 text-left text-[11px] font-medium tracking-[0.08em] uppercase ${className}`}>{children}</th>;
}

function FilterChip({ label, active, onClick, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`px-3 h-9 rounded-md text-sm font-medium transition-colors ${
                active ? "bg-[#131B2F] text-white" : "text-[#4B5565] hover:bg-[#F5F5F7]"
            }`}
        >
            {label}
        </button>
    );
}

function FilterPill({ label, active, onClick, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`inline-flex items-center gap-1 h-8 px-3 rounded-full text-xs font-medium border transition-colors ${
                active
                    ? "bg-[#131B2F] text-white border-[#131B2F]"
                    : "bg-white text-[#131B2F] border-[#E5E7EB] hover:bg-[#F5F5F7]"
            }`}
        >
            {label}
        </button>
    );
}

function RowBtn({ label, icon: Icon, onClick, disabled, testid, tone = "default" }) {
    const toneCls = {
        default: "border-[#E5E7EB] text-[#131B2F] hover:bg-[#F5F5F7]",
        amber:   "border-[#F59E0B]/25 text-[#B45309] hover:bg-[#F59E0B]/8",
        green:   "border-[#3E9E2E]/25 text-[#3E9E2E] hover:bg-[#3E9E2E]/8",
    }[tone];
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            data-testid={testid}
            className={`inline-flex items-center gap-1.5 px-2.5 h-9 rounded-md border bg-white text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${toneCls}`}
        >
            <Icon size={13} strokeWidth={1.75} /> {label}
        </button>
    );
}


// FIELD-001C1B · Resource Management "View resource" summary modal.
// Non-administrative, read-only. Reuses existing eng data + deep-links
// into the canonical Tasks and Calendar routes with URL query state so
// refresh + browser Back preserve the filter.
function ResourceSummaryModal({ eng, onClose, onDrillTasks, onDrillCalendar }) {
    if (!eng) return null;
    const workingDays = eng.workingDays || [1, 2, 3, 4, 5];
    const today = new Date().toISOString().slice(0, 10);
    const upcomingHolidays = (eng.holidays || []).filter((d) => d >= today).sort().slice(0, 6);
    const kind = eng.kind || "internal_person";
    const resourceType = effectiveResourceType(eng);
    const orgLine = eng.organisationName || (kind === "external_organisation" ? eng.displayName : null);
    const DAY_SHORT = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
    return (
        <div className="fixed inset-0 z-50 bg-black/55 flex items-center justify-center px-4"
             data-testid="resource-summary-modal"
             role="dialog"
             aria-labelledby="resource-summary-title">
            <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto shadow-2xl">
                <div className="px-6 py-5 border-b border-[#E5E7EB] flex items-start justify-between">
                    <div>
                        <div id="resource-summary-title" className="text-xl text-[#131B2F] font-medium"
                             data-testid="resource-summary-name">
                            {eng.displayName}
                        </div>
                        <div className="text-xs text-[#4B5565] mt-0.5" data-testid="resource-summary-classification">
                            {resourceType}{orgLine && kind !== "external_organisation" ? ` · ${orgLine}` : ""}
                        </div>
                    </div>
                    <button onClick={onClose} data-testid="resource-summary-close"
                            className="w-9 h-9 rounded-full bg-[#F5F5F7] flex items-center justify-center text-[#4B5565]">
                        <User size={14} strokeWidth={1.75} />
                    </button>
                </div>

                <div className="p-6 space-y-5">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.12em] text-[#4B5565]">Kind</div>
                            <div className="text-[#131B2F]" data-testid="resource-summary-kind">
                                {kind.replace(/_/g, " ")}
                            </div>
                        </div>
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.12em] text-[#4B5565]">Portal access</div>
                            <div className="text-[#131B2F]" data-testid="resource-summary-portal">
                                {(eng.portalAccess || "enabled").replace(/_/g, " ")}
                            </div>
                        </div>
                        {kind === "external_individual" && orgLine && (
                            <div className="col-span-2">
                                <div className="text-[11px] uppercase tracking-[0.12em] text-[#4B5565]">Organisation</div>
                                <div className="text-[#131B2F]" data-testid="resource-summary-org">{orgLine}</div>
                            </div>
                        )}
                    </div>

                    {kind !== "external_organisation" && (
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.12em] text-[#4B5565] mb-1.5">Availability</div>
                        <div className="flex gap-1.5 flex-wrap" data-testid="resource-summary-days">
                            {DAY_SHORT.map((short, idx) => {
                                const on = (workingDays || []).includes(idx + 1);
                                return (
                                    <span
                                        key={short}
                                        data-testid={`resource-summary-day-${idx + 1}${on ? "-on" : "-off"}`}
                                        className={`inline-flex items-center justify-center min-w-[32px] h-7 px-2 rounded-md text-xs font-medium ${
                                            on
                                                ? "bg-[#3E9E2E]/12 text-[#3E9E2E] border border-[#3E9E2E]/35"
                                                : "bg-[#F5F5F7] text-[#9CA3AF] border border-transparent"
                                        }`}
                                    >
                                        {short}
                                    </span>
                                );
                            })}
                        </div>
                        <div className="mt-2 text-[11px] uppercase tracking-[0.12em] text-[#4B5565]">Upcoming holidays</div>
                        {upcomingHolidays.length === 0 ? (
                            <div className="text-xs text-[#4B5565]" data-testid="resource-summary-holidays-empty">
                                No upcoming holidays.
                            </div>
                        ) : (
                            <ul className="mt-1 flex gap-1.5 flex-wrap" data-testid="resource-summary-holidays">
                                {upcomingHolidays.map((d) => (
                                    <li key={d}
                                        data-testid={`resource-summary-holiday-${d}`}
                                        className="inline-flex items-center h-7 px-2 rounded-md bg-[#F5F5F7] text-xs text-[#131B2F] border border-[#E5E7EB]">
                                        {d}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    )}

                    <div>
                        <div className="text-[11px] uppercase tracking-[0.12em] text-[#4B5565] mb-1.5">Assignments & schedule</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <button data-testid="resource-summary-view-tasks"
                                    onClick={() => onDrillTasks?.()}
                                    className="pe-btn-outline justify-center h-11">
                                View open tasks ({eng.openTaskCount ?? 0})
                            </button>
                            <button data-testid="resource-summary-view-overdue"
                                    onClick={() => onDrillTasks?.({ scope: "overdue" })}
                                    className="pe-btn-outline justify-center h-11">
                                Overdue ({eng.overdueTaskCount ?? 0})
                            </button>
                            <button data-testid="resource-summary-view-calendar"
                                    onClick={() => onDrillCalendar?.({ focus: "week" })}
                                    className="pe-btn-outline justify-center h-11">
                                View calendar this week
                            </button>
                            <button data-testid="resource-summary-open-schedule"
                                    onClick={() => onDrillCalendar?.({ focus: "today" })}
                                    className="pe-btn-primary justify-center h-11">
                                Open schedule
                            </button>
                        </div>
                    </div>
                </div>

                <div className="px-6 pb-6">
                    <button onClick={onClose} data-testid="resource-summary-done"
                            className="pe-btn-outline justify-center h-11 w-full">
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
}
